/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { FileText, Download, FileSignature } from 'lucide-react';
import { listModulistica, getModulisticaDetail } from '@/lib/proToolkit';

const CATEGORY_LABEL = {
  consensi: 'Consensi',
  certificati: 'Certificati',
  prescrizioni: 'Prescrizioni',
  referti: 'Referti',
};

const FIELD_LABEL = {
  paziente_nome: 'Nome e cognome paziente',
  paziente_cf: 'Codice fiscale paziente',
  paziente_dob: 'Data di nascita paziente',
  prestazione: 'Prestazione erogata',
  diagnosi: 'Diagnosi',
  giorni_prognosi: 'Giorni di prognosi',
  attivita: 'Attivita sportiva',
  farmaci: 'Farmaci prescritti (uno per riga)',
  anamnesi: 'Anamnesi patologica prossima',
  esame_obiettivo: 'Esame obiettivo',
  terapia: 'Terapia consigliata',
  diagnosi_dimissione: 'Diagnosi di dimissione',
  follow_up: 'Piano di follow-up',
  esami_richiesti: 'Esami richiesti',
  quesito_diagnostico: 'Quesito diagnostico',
};

const LONG_TEXT_FIELDS = new Set([
  'farmaci', 'anamnesi', 'esame_obiettivo', 'terapia',
  'follow_up', 'esami_richiesti',
]);

const ModuloForm = ({ doc, onCancel }) => {
  const [values, setValues] = useState({});
  const [detail, setDetail] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await getModulisticaDetail(doc.id);
        setDetail(d);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Errore nel caricamento del modulo');
      }
    })();
  }, [doc.id]);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const canGenerate = doc.required_fields.every((f) => (values[f] || '').trim().length > 0);

  const generatePdf = () => {
    if (!detail) return;
    setGenerating(true);
    try {
      const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
      const marginX = 48;
      let y = 60;
      const lineH = 16;

      const writeLine = (text, opts = {}) => {
        pdf.setFont('helvetica', opts.bold ? 'bold' : 'normal');
        pdf.setFontSize(opts.size || 11);
        const wrapped = pdf.splitTextToSize(text, 500);
        wrapped.forEach((ln) => {
          if (y > 780) { pdf.addPage(); y = 60; }
          pdf.text(ln, marginX, y);
          y += lineH;
        });
      };

      // Header
      writeLine(doc.title, { bold: true, size: 16 });
      y += 4;
      writeLine(`Base legale: ${doc.legal_basis}`, { size: 9 });
      y += 10;

      // Doctor block
      const dr = detail.doctor_prefill || {};
      writeLine('MEDICO', { bold: true, size: 11 });
      writeLine(`Dr. ${dr.full_name || '—'}`);
      if (dr.specialty) writeLine(`Specialita: ${dr.specialty}`);
      if (dr.albo_number) writeLine(`Albo: ${dr.albo_number}${dr.albo_province ? ` (${dr.albo_province})` : ''}`);
      if (dr.email) writeLine(`Email: ${dr.email}`);
      if (dr.phone) writeLine(`Tel: ${dr.phone}`);
      y += 8;

      // Patient block (if present)
      writeLine('PAZIENTE', { bold: true, size: 11 });
      if (values.paziente_nome) writeLine(`Nome: ${values.paziente_nome}`);
      if (values.paziente_cf) writeLine(`Codice fiscale: ${values.paziente_cf}`);
      if (values.paziente_dob) writeLine(`Data di nascita: ${values.paziente_dob}`);
      y += 8;

      // Body: all remaining fields
      const skipInPatient = new Set(['paziente_nome', 'paziente_cf', 'paziente_dob']);
      writeLine('CONTENUTO', { bold: true, size: 11 });
      Object.entries(values).forEach(([k, v]) => {
        if (skipInPatient.has(k)) return;
        if (!v || !String(v).trim()) return;
        writeLine(`${FIELD_LABEL[k] || k}:`, { bold: true });
        writeLine(String(v));
        y += 4;
      });
      y += 12;

      // Signature area
      const today = new Date().toLocaleDateString('it-IT');
      writeLine(`Data: ${today}`);
      y += 30;
      pdf.line(marginX, y, marginX + 200, y);
      writeLine('Firma del medico', { size: 9 });

      if (['consenso_informato_generico', 'consenso_telemedicina'].includes(doc.id)) {
        y += 40;
        pdf.line(marginX + 260, y - 40, marginX + 460, y - 40);
        pdf.setFontSize(9);
        pdf.text('Firma del paziente', marginX + 260, y - 25);
      }

      const filename = `${doc.id}_${(values.paziente_nome || 'paziente').replace(/\s+/g, '_')}.pdf`;
      pdf.save(filename);
      toast.success('PDF generato con successo');
    } catch (e) {
       
      console.error('[Modulistica] pdf generation failed', e);
      toast.error('Errore nella generazione del PDF');
    } finally {
      setGenerating(false);
    }
  };

  if (!detail) {
    return <Skeleton className="h-40 rounded-xl" />;
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {doc.required_fields.map((field) => (
          <div key={field}>
            <Label htmlFor={`mod-${field}`} className="text-xs">
              {FIELD_LABEL[field] || field} *
            </Label>
            {LONG_TEXT_FIELDS.has(field) ? (
              <Textarea
                id={`mod-${field}`}
                rows={3}
                value={values[field] || ''}
                onChange={(e) => set(field, e.target.value)}
                data-testid={`modulistica-field-${field}`}
              />
            ) : (
              <Input
                id={`mod-${field}`}
                value={values[field] || ''}
                onChange={(e) => set(field, e.target.value)}
                data-testid={`modulistica-field-${field}`}
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
        <Button variant="ghost" onClick={onCancel}>Annulla</Button>
        <Button
          onClick={generatePdf}
          disabled={!canGenerate || generating}
          data-testid="modulistica-generate-btn"
        >
          <Download className="h-4 w-4 mr-1" />
          {generating ? 'Generazione…' : 'Genera PDF'}
        </Button>
      </div>
    </div>
  );
};

export const ModulisticaLibrary = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await listModulistica();
        setData(d);
      } catch {
        setData({ categories: [], documents: [] });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={`mod-sk-${i}`} className="h-32 rounded-xl" />
        ))}
      </div>
    );
  }

  if (!data) return null;

  const grouped = {};
  data.documents.forEach((d) => {
    if (!grouped[d.category]) grouped[d.category] = [];
    grouped[d.category].push(d);
  });

  return (
    <div className="space-y-6" data-testid="modulistica-library">
      {Object.entries(grouped).map(([cat, items]) => (
        <section key={cat}>
          <h3 className="font-display font-semibold text-base mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            {CATEGORY_LABEL[cat] || cat}
            <Badge variant="secondary" className="text-xs">{items.length}</Badge>
          </h3>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {items.map((doc) => (
              <Card
                key={doc.id}
                className="cursor-pointer hover:shadow-elev-md transition-shadow"
                onClick={() => setSelected(doc)}
                data-testid={`modulistica-card-${doc.id}`}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-display">{doc.title}</CardTitle>
                  <CardDescription className="text-xs">{doc.description}</CardDescription>
                </CardHeader>
                <CardContent className="pt-0 pb-3 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span className="truncate">{doc.legal_basis}</span>
                  <FileSignature className="h-3.5 w-3.5 flex-shrink-0 ml-1" />
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      ))}

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{selected?.title}</DialogTitle>
            <DialogDescription>{selected?.description}</DialogDescription>
          </DialogHeader>
          {selected && (
            <ModuloForm doc={selected} onCancel={() => setSelected(null)} />
          )}
          <DialogFooter />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModulisticaLibrary;
